import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getFieldErrors, getFormError } from 'lib/selectors';
import { useMakePayment, useShowFeesModal } from 'lib/hooks';
import {
    PAYMENT_MAKE_PAYMENT,
    PAYMENT_MAKE_PAYMENT_SUCCESS,
} from 'constants/shared/routes';
import { hideModal, showModal } from 'actions/modal';
import {
    CONFIRM_AND_SEND_MODAL,
    ERROR_MODAL,
    PAYMENT_FEE_MODAL,
} from 'constants/shared/modalTypes';
import { dispatchHideModal } from 'utils/modals';
import PageContainer from 'components/shared/generic/pageContainer/presentational/PageContainer';
import MakePaymentForm from '../presentational/MakePaymentForm';
import SectionTitle from 'components/shared/generic/sectionTitle/presentational/SectionTitle';
import { calculateTransactionFee, usePrevious } from 'lib/utils';
import {
    REACT_APP_SUITSME_SORTCODE_IDT,
    REACT_APP_SUITSME_SORTCODE_EPS,
} from 'config';
import { trackCustomerIOEvent } from 'utils/customerIO';
import { useGetFeesQuery } from 'lib/services/fees';
import { CustomerIOEvent } from 'lib/type';
import { selectPayee } from 'lib/slices';

const MakePaymentFormContainer = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const selectedPayee = useSelector(selectPayee);
    const { data: allFees } = useGetFeesQuery();
    const { internalTransactionFee = 0, fasterFeePercentage } = allFees || {};
    const isInternalPayee =
        selectedPayee?.sortCode === REACT_APP_SUITSME_SORTCODE_IDT ||
        selectedPayee?.sortCode === REACT_APP_SUITSME_SORTCODE_EPS;
    const {
        form,
        handleChange,
        handleSubmit,
        isLoading,
        error,
    } = useMakePayment(onSubmit, onError);

    const fieldErrors = useSelector(getFieldErrors);
    const formError = useSelector(getFormError);

    const prevState = usePrevious({ fieldErrors, formError });

    const handleCloseModal = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    useEffect(() => {
        const isNewFormError = formError && !prevState?.formError;
        const isNewFieldErrors =
            Object.keys(fieldErrors as object)?.length >
            Object.keys(prevState?.fieldErrors as object).length;

        if (isNewFormError || isNewFieldErrors) {
            handleCloseModal();
        }
    }, [fieldErrors, formError, prevState, handleCloseModal]);

    const transactionFee = isInternalPayee
        ? internalTransactionFee
        : calculateTransactionFee(+form.amount, fasterFeePercentage);

    const { shouldShowFeesModal } = useShowFeesModal(
        +form.amount,
        transactionFee,
        isInternalPayee,
    );

    const handleConfirmModal = () => {
        if (!form.amount || form.amount === '£0.00') {
            dispatch(
                showModal({
                    type: ERROR_MODAL,
                    props: {
                        title: 'Invalid amount',
                        message: 'Please enter an amount larger than £0.00',
                        onClose: () => handleCloseModal(),
                    },
                }),
            );
        } else if (shouldShowFeesModal) {
            dispatch(
                showModal({
                    type: PAYMENT_FEE_MODAL,
                    props: {
                        amount: +form.amount,
                        fee: transactionFee,
                        handleSubmit: handleShowConfirmAndSendModal,
                        closeModal: handleCloseModal,
                        isInternalPayee,
                    },
                }),
            );
        } else {
            handleShowConfirmAndSendModal();
        }
    };

    const handleShowConfirmAndSendModal = () => {
        dispatch(
            showModal({
                type: CONFIRM_AND_SEND_MODAL,
                props: {
                    form,
                    selectedPayee,
                    handleSubmit,
                    error,
                    isPosting: isLoading,
                    type: 'make-payment',
                },
            }),
        );
    };

    function onSubmit() {
        dispatchHideModal(dispatch);
        history.push(PAYMENT_MAKE_PAYMENT_SUCCESS, {
            data: {
                amount: form.amount,
                recipient: selectedPayee.payeeName,
            },
        });
        const formattedAmount = Number.parseInt(
            (Number(form.amount) * 100).toFixed(0),
        );
        trackCustomerIOEvent(CustomerIOEvent.MAKE_PAYMENT, {
            payeeName: selectedPayee.payeeName,
            amount: -formattedAmount,
        });
    }

    function onError() {
        dispatchHideModal(dispatch);
        history.push(PAYMENT_MAKE_PAYMENT_SUCCESS, {
            data: {
                error,
            },
        });
    }

    useEffect(() => {
        if (!selectedPayee) {
            history.push(PAYMENT_MAKE_PAYMENT);
        }
    }, [history, selectedPayee]);

    return (
        <PageContainer className="make-payment-form-container">
            <SectionTitle
                title={selectedPayee?.payeeName}
                backRoute={PAYMENT_MAKE_PAYMENT}
                additionalText={`${selectedPayee?.accountNo} | ${selectedPayee?.sortCode}`}
            />
            <MakePaymentForm
                {...form}
                handleChange={handleChange}
                handleSubmit={handleConfirmModal}
                isPosting={isLoading}
            />
        </PageContainer>
    );
};

export default MakePaymentFormContainer;
